/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useRef, useMemo } from "react";
import {
  BookOpen,
  Plus,
  Trash2,
  Power,
  Pencil,
  Image as ImageIcon,
  Heart,
  Bookmark,
  X,
  GripVertical,
  RefreshCw,
  Search,
  TrendingUp,
  Sparkles,
  LayoutGrid,
  List as ListIcon,
  Calendar,
  Eye,
  EyeOff,
} from "lucide-react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  useStories,
  useCreateStory,
  useUpdateStory,
  useToggleStory,
  useDeleteStory,
  uploadStoryImage,
  Story,
  CreateStoryPayload,
} from "@/hooks/api/useStories";
import { toast } from "react-toastify";
import { cn } from "@/lib/utils";

// ─── Categories ──────────────────────────────────────────────────────────────

type Category =
  | "PLAYER_STORY"
  | "BREAKING_NEWS"
  | "TRANSFER"
  | "MATCH_RECAP"
  | "TRIVIA";

const CATEGORY_LABELS: Record<
  Category,
  { label: string; pill: string; ring: string; dot: string }
> = {
  PLAYER_STORY: {
    label: "Player Story",
    pill: "bg-blue-100 text-blue-700",
    ring: "ring-blue-400",
    dot: "bg-blue-500",
  },
  BREAKING_NEWS: {
    label: "Breaking News",
    pill: "bg-red-100 text-red-700",
    ring: "ring-red-400",
    dot: "bg-red-500",
  },
  TRANSFER: {
    label: "Transfer",
    pill: "bg-purple-100 text-purple-700",
    ring: "ring-purple-400",
    dot: "bg-purple-500",
  },
  MATCH_RECAP: {
    label: "Match Recap",
    pill: "bg-emerald-100 text-emerald-700",
    ring: "ring-emerald-400",
    dot: "bg-emerald-500",
  },
  TRIVIA: {
    label: "Trivia",
    pill: "bg-amber-100 text-amber-700",
    ring: "ring-amber-400",
    dot: "bg-amber-500",
  },
};

type StatusFilter = "all" | "published" | "draft";
type SortKey = "newest" | "oldest" | "most-liked" | "most-saved";

// ─── Slide form helpers (unchanged) ──────────────────────────────────────────

interface SlideForm {
  imageUrl: string;
  videoUrl: string;
  linkUrl: string;
  linkText: string;
  content: string;
  order: number;
  uploading?: boolean;
}

const emptySlide: SlideForm = {
  imageUrl: "",
  videoUrl: "",
  linkUrl: "",
  linkText: "",
  content: "",
  order: 0,
};

const emptyForm = {
  title: "",
  subtitle: "",
  category: "PLAYER_STORY" as Category,
  coverImage: "",
  isPublished: false,
  slides: [{ ...emptySlide }] as SlideForm[],
};

// ─── Page ────────────────────────────────────────────────────────────────────

export default function StoriesPage() {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Story | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState<{ title?: string; slides?: string }>({});
  const [coverUploading, setCoverUploading] = useState(false);
  const coverInputRef = useRef<HTMLInputElement>(null);

  // Filter / view state
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<"all" | Category>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [sortKey, setSortKey] = useState<SortKey>("newest");
  const [view, setView] = useState<"grid" | "list">("grid");

  const { data, isLoading, refetch, isFetching } = useStories();
  const { mutate: create, isPending: isCreating } = useCreateStory();
  const { mutate: update, isPending: isUpdating } = useUpdateStory();
  const { mutate: toggle } = useToggleStory();
  const { mutate: remove } = useDeleteStory();

  const stories = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  // ── Derived stats ───────────────────────────────────────────────────────
  const totalLikes = stories.reduce((s, x) => s + (x._count?.likes ?? 0), 0);
  const totalSaves = stories.reduce((s, x) => s + (x._count?.saves ?? 0), 0);
  const publishedCount = stories.filter((s) => s.isPublished).length;
  const draftCount = stories.length - publishedCount;
  const publishRate = stories.length
    ? Math.round((publishedCount / stories.length) * 100)
    : 0;
  const startOfMonth = new Date();
  startOfMonth.setDate(1);
  startOfMonth.setHours(0, 0, 0, 0);
  const thisMonth = stories.filter(
    (s) => s.publishedAt && new Date(s.publishedAt) >= startOfMonth,
  ).length;

  // ── Filter + sort ───────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const out = stories.filter((s) => {
      if (categoryFilter !== "all" && s.category !== categoryFilter)
        return false;
      if (statusFilter === "published" && !s.isPublished) return false;
      if (statusFilter === "draft" && s.isPublished) return false;
      if (q) {
        const hay = `${s.title} ${s.subtitle ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    out.sort((a, b) => {
      if (sortKey === "newest")
        return (
          new Date(b.publishedAt || b.createdAt || 0).getTime() -
          new Date(a.publishedAt || a.createdAt || 0).getTime()
        );
      if (sortKey === "oldest")
        return (
          new Date(a.publishedAt || a.createdAt || 0).getTime() -
          new Date(b.publishedAt || b.createdAt || 0).getTime()
        );
      if (sortKey === "most-liked")
        return (b._count?.likes ?? 0) - (a._count?.likes ?? 0);
      return (b._count?.saves ?? 0) - (a._count?.saves ?? 0);
    });
    return out;
  }, [stories, search, categoryFilter, statusFilter, sortKey]);

  // ── Dialog helpers (unchanged flow) ─────────────────────────────────────
  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (s: Story) => {
    setEditing(s);
    setForm({
      title: s.title,
      subtitle: s.subtitle || "",
      category: s.category as Category,
      coverImage: s.coverImage || "",
      isPublished: s.isPublished,
      slides: s.slides.length
        ? s.slides.map((sl) => ({
            imageUrl: sl.imageUrl || "",
            videoUrl: sl.videoUrl || "",
            linkUrl: sl.linkUrl || "",
            linkText: sl.linkText || "",
            content: sl.content,
            order: sl.order,
          }))
        : [{ ...emptySlide }],
    });
    setOpen(true);
  };

  const closeDialog = () => {
    setOpen(false);
    setEditing(null);
    setErrors({});
  };

  const handleSave = () => {
    const newErrors: typeof errors = {};
    if (!form.title.trim()) newErrors.title = "Title is required";
    if (
      form.slides.every((s) => !s.content.trim() && !s.imageUrl && !s.videoUrl)
    )
      newErrors.slides =
        "At least one slide must have content, an image, or a video";
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }
    setErrors({});

    const payload: CreateStoryPayload = {
      title: form.title,
      subtitle: form.subtitle || undefined,
      category: form.category,
      coverImage: form.coverImage || undefined,
      isPublished: form.isPublished,
      slides: form.slides
        .filter((s) => s.content.trim() || s.imageUrl || s.videoUrl)
        .map((s, i) => ({
          imageUrl: s.imageUrl || undefined,
          videoUrl: s.videoUrl || undefined,
          linkUrl: s.linkUrl || undefined,
          linkText: s.linkText || undefined,
          content: s.content,
          order: i,
        })),
    };

    if (editing) {
      update(
        { id: editing.id, data: payload },
        {
          onSuccess: () => {
            toast.success("Story updated");
            closeDialog();
          },
          onError: (e: any) =>
            toast.error(e.response?.data?.message || "Failed"),
        },
      );
    } else {
      create(payload, {
        onSuccess: () => {
          toast.success("Story created");
          closeDialog();
        },
        onError: (e: any) =>
          toast.error(e.response?.data?.message || "Failed"),
      });
    }
  };

  const handleDelete = (s: Story) => {
    if (!confirm(`Delete "${s.title}"?`)) return;
    remove(s.id, {
      onSuccess: () => toast.success("Deleted"),
      onError: (e: any) => toast.error(e.response?.data?.message || "Failed"),
    });
  };

  const handleToggle = (id: number) => {
    toggle(id, {
      onError: (e: any) => toast.error(e.response?.data?.message || "Failed"),
    });
  };

  // ── Upload handlers ─────────────────────────────────────────────────────
  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverUploading(true);
    try {
      const { url } = await uploadStoryImage(file);
      setForm((f) => ({ ...f, coverImage: url }));
    } catch {
      toast.error("Image upload failed");
    } finally {
      setCoverUploading(false);
    }
  };

  const addSlide = () => {
    setForm((f) => ({
      ...f,
      slides: [...f.slides, { ...emptySlide, order: f.slides.length }],
    }));
  };

  const removeSlide = (idx: number) => {
    setForm((f) => ({
      ...f,
      slides: f.slides
        .filter((_, i) => i !== idx)
        .map((s, i) => ({ ...s, order: i })),
    }));
  };

  const updateSlide = (
    idx: number,
    field: keyof SlideForm,
    value: string,
  ) => {
    setForm((f) => ({
      ...f,
      slides: f.slides.map((s, i) =>
        i === idx ? { ...s, [field]: value } : s,
      ),
    }));
  };

  const handleSlideImageUpload = async (idx: number, file: File) => {
    setForm((f) => ({
      ...f,
      slides: f.slides.map((s, i) =>
        i === idx ? { ...s, uploading: true } : s,
      ),
    }));
    try {
      const { url } = await uploadStoryImage(file);
      updateSlide(idx, "imageUrl", url);
    } catch {
      toast.error("Image upload failed");
    } finally {
      setForm((f) => ({
        ...f,
        slides: f.slides.map((s, i) =>
          i === idx ? { ...s, uploading: false } : s,
        ),
      }));
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────
  const activeFilterCount =
    (categoryFilter !== "all" ? 1 : 0) +
    (statusFilter !== "all" ? 1 : 0) +
    (search.trim() ? 1 : 0);

  return (
    <Layout>
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
              <BookOpen className="h-5 w-5 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Stories</h1>
          </div>
          <p className="text-sm text-gray-500 ml-[52px]">
            Player stories, breaking news, transfers & trivia — publish to the mobile feed.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() =>
              refetch().then(() => toast.success("Refreshed"))
            }
            disabled={isFetching}
            title="Refresh stories"
          >
            <RefreshCw
              className={cn("h-4 w-4", isFetching && "animate-spin")}
            />
          </Button>
          <Button
            onClick={openCreate}
            className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
          >
            <Plus className="h-4 w-4" /> New Story
          </Button>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <KpiTile
          label="Total Stories"
          value={stories.length}
          sub={`${publishedCount} live · ${draftCount} drafts`}
          icon={<BookOpen className="h-5 w-5" />}
          tone="emerald"
        />
        <KpiTile
          label="This Month"
          value={thisMonth}
          sub={`published since ${startOfMonth.toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
          })}`}
          icon={<Sparkles className="h-5 w-5" />}
          tone="amber"
        />
        <KpiTile
          label="Total Likes"
          value={totalLikes.toLocaleString()}
          sub="across all stories"
          icon={<Heart className="h-5 w-5" />}
          tone="red"
        />
        <KpiTile
          label="Total Saves"
          value={totalSaves.toLocaleString()}
          sub={`${publishRate}% of stories live`}
          icon={<Bookmark className="h-5 w-5" />}
          tone="blue"
        />
      </div>

      {/* Category chips */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button
          onClick={() => setCategoryFilter("all")}
          className={cn(
            "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all",
            categoryFilter === "all"
              ? "bg-gray-900 text-white border-gray-900"
              : "bg-white text-gray-600 border-gray-200 hover:border-gray-400",
          )}
        >
          All
          <span className="text-[10px] opacity-80">{stories.length}</span>
        </button>
        {(Object.keys(CATEGORY_LABELS) as Category[]).map((cat) => {
          const count = stories.filter((s) => s.category === cat).length;
          const conf = CATEGORY_LABELS[cat];
          const active = categoryFilter === cat;
          return (
            <button
              key={cat}
              onClick={() => setCategoryFilter(cat)}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all",
                active
                  ? `${conf.pill} border-transparent ring-2 ${conf.ring}`
                  : "bg-white text-gray-600 border-gray-200 hover:border-gray-400",
              )}
            >
              <span
                className={cn("w-1.5 h-1.5 rounded-full", conf.dot)}
              />
              {conf.label}
              <span className="text-[10px] opacity-80">{count}</span>
            </button>
          );
        })}
      </div>

      {/* Toolbar: search + status + sort + view toggle */}
      <div className="bg-white rounded-xl border border-gray-200 p-3 flex flex-wrap items-center gap-2 mb-5">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title or subtitle…"
            className="pl-9 h-9 text-sm"
          />
        </div>

        <Select
          value={statusFilter}
          onValueChange={(v) => setStatusFilter(v as StatusFilter)}
        >
          <SelectTrigger className="h-9 w-[140px] text-sm">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All status</SelectItem>
            <SelectItem value="published">Published</SelectItem>
            <SelectItem value="draft">Drafts</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={sortKey}
          onValueChange={(v) => setSortKey(v as SortKey)}
        >
          <SelectTrigger className="h-9 w-[160px] text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest first</SelectItem>
            <SelectItem value="oldest">Oldest first</SelectItem>
            <SelectItem value="most-liked">Most liked</SelectItem>
            <SelectItem value="most-saved">Most saved</SelectItem>
          </SelectContent>
        </Select>

        <div className="flex bg-gray-100 rounded-lg p-0.5">
          <button
            onClick={() => setView("grid")}
            className={cn(
              "px-2.5 py-1.5 rounded-md text-xs font-semibold transition-all",
              view === "grid"
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-500 hover:text-gray-700",
            )}
            title="Grid view"
          >
            <LayoutGrid className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => setView("list")}
            className={cn(
              "px-2.5 py-1.5 rounded-md text-xs font-semibold transition-all",
              view === "list"
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-500 hover:text-gray-700",
            )}
            title="List view"
          >
            <ListIcon className="h-3.5 w-3.5" />
          </button>
        </div>

        {activeFilterCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="text-gray-500 text-xs"
            onClick={() => {
              setSearch("");
              setCategoryFilter("all");
              setStatusFilter("all");
            }}
          >
            <X className="h-3 w-3 mr-1" /> Clear ({activeFilterCount})
          </Button>
        )}
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-500" />
        </div>
      ) : stories.length === 0 ? (
        <EmptyState onCreate={openCreate} />
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-gray-300 rounded-xl py-16 text-center text-gray-400">
          <Search className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p className="font-semibold text-gray-500">No stories match those filters</p>
          <p className="text-sm">Try clearing search or filters.</p>
        </div>
      ) : view === "grid" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map((s) => (
            <StoryGridCard
              key={s.id}
              story={s}
              onEdit={openEdit}
              onToggle={handleToggle}
              onDelete={handleDelete}
            />
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((s) => (
            <StoryListRow
              key={s.id}
              story={s}
              onEdit={openEdit}
              onToggle={handleToggle}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {/* ── Create / Edit Dialog (unchanged structure) ──────────────────── */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Story" : "New Story"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Title & Subtitle */}
            <div>
              <Label className="mb-1 block text-sm">
                Title <span className="text-red-500">*</span>
              </Label>
              <Input
                value={form.title}
                onChange={(e) => {
                  setForm((f) => ({ ...f, title: e.target.value }));
                  if (e.target.value.trim())
                    setErrors((er) => ({ ...er, title: undefined }));
                }}
                placeholder="e.g. Mbappe's Journey to Real Madrid"
                className={errors.title ? "border-red-400" : ""}
              />
              {errors.title && (
                <p className="text-xs text-red-500 mt-1 font-medium">
                  {errors.title}
                </p>
              )}
            </div>
            <div>
              <Label className="mb-1 block text-sm">Subtitle (optional)</Label>
              <Input
                value={form.subtitle}
                onChange={(e) =>
                  setForm((f) => ({ ...f, subtitle: e.target.value }))
                }
                placeholder="A short description shown below the title"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-1 block text-sm">Category</Label>
                <Select
                  value={form.category}
                  onValueChange={(v) =>
                    setForm((f) => ({ ...f, category: v as Category }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(CATEGORY_LABELS) as Category[]).map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {CATEGORY_LABELS[cat].label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1 block text-sm">Status</Label>
                <Select
                  value={form.isPublished ? "published" : "draft"}
                  onValueChange={(v) =>
                    setForm((f) => ({ ...f, isPublished: v === "published" }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Cover */}
            <div>
              <Label className="mb-1 block text-sm">Cover Image</Label>
              <div className="flex items-center gap-3">
                {form.coverImage ? (
                  <div className="relative w-24 h-16 rounded-lg overflow-hidden border">
                    <img
                      src={form.coverImage}
                      alt="Cover"
                      className="w-full h-full object-cover"
                    />
                    <button
                      className="absolute top-0 right-0 bg-black/50 rounded-bl p-0.5"
                      onClick={() =>
                        setForm((f) => ({ ...f, coverImage: "" }))
                      }
                    >
                      <X className="h-3 w-3 text-white" />
                    </button>
                  </div>
                ) : null}
                <Button
                  variant="outline"
                  size="sm"
                  disabled={coverUploading}
                  onClick={() => coverInputRef.current?.click()}
                >
                  <ImageIcon className="h-4 w-4 mr-1" />
                  {coverUploading ? "Uploading..." : "Upload Cover"}
                </Button>
                <input
                  ref={coverInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleCoverUpload}
                />
              </div>
            </div>

            {/* Slides */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label className="text-sm font-semibold">
                  Slides ({form.slides.length}){" "}
                  <span className="text-red-500">*</span>
                </Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    addSlide();
                    setErrors((er) => ({ ...er, slides: undefined }));
                  }}
                >
                  <Plus className="h-3 w-3 mr-1" /> Add Slide
                </Button>
              </div>
              {errors.slides && (
                <p className="text-xs text-red-500 mb-2 font-medium">
                  {errors.slides}
                </p>
              )}
              <div className="space-y-3">
                {form.slides.map((slide, idx) => (
                  <div
                    key={idx}
                    className="border rounded-lg p-3 bg-gray-50 relative group"
                  >
                    <div className="flex items-start gap-2">
                      <GripVertical className="h-4 w-4 text-gray-300 mt-2 shrink-0" />
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-gray-400 w-6">
                            #{idx + 1}
                          </span>
                          <div className="flex items-center gap-2 flex-1">
                            {slide.imageUrl ? (
                              <div className="relative w-16 h-12 rounded overflow-hidden border shrink-0">
                                <img
                                  src={slide.imageUrl}
                                  alt={`Slide ${idx + 1}`}
                                  className="w-full h-full object-cover"
                                />
                                <button
                                  className="absolute top-0 right-0 bg-black/50 rounded-bl p-0.5"
                                  onClick={() => updateSlide(idx, "imageUrl", "")}
                                >
                                  <X className="h-2.5 w-2.5 text-white" />
                                </button>
                              </div>
                            ) : null}
                            <label className="cursor-pointer text-xs text-blue-600 hover:underline">
                              {slide.uploading
                                ? "Uploading..."
                                : slide.imageUrl
                                  ? "Change"
                                  : "+ Image"}
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => {
                                  const f = e.target.files?.[0];
                                  if (f) handleSlideImageUpload(idx, f);
                                }}
                              />
                            </label>
                          </div>
                          {form.slides.length > 1 && (
                            <button
                              onClick={() => removeSlide(idx)}
                              className="opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="h-4 w-4 text-red-400" />
                            </button>
                          )}
                        </div>
                        <textarea
                          value={slide.content}
                          onChange={(e) =>
                            updateSlide(idx, "content", e.target.value)
                          }
                          placeholder="Slide content (text shown to users)..."
                          rows={2}
                          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            value={slide.videoUrl}
                            onChange={(e) =>
                              updateSlide(idx, "videoUrl", e.target.value)
                            }
                            placeholder="Video URL (YouTube / MP4)"
                            className="rounded-md border border-gray-300 px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          />
                          <div className="flex gap-1">
                            <input
                              value={slide.linkUrl}
                              onChange={(e) =>
                                updateSlide(idx, "linkUrl", e.target.value)
                              }
                              placeholder="Link URL"
                              className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            />
                            <input
                              value={slide.linkText}
                              onChange={(e) =>
                                updateSlide(idx, "linkText", e.target.value)
                              }
                              placeholder="Label"
                              className="w-20 rounded-md border border-gray-300 px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isCreating || isUpdating}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {isCreating || isUpdating
                ? "Saving..."
                : editing
                  ? "Update"
                  : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

// ─── Subcomponents ───────────────────────────────────────────────────────────

function KpiTile({
  label,
  value,
  sub,
  icon,
  tone,
}: {
  label: string;
  value: number | string;
  sub: string;
  icon: React.ReactNode;
  tone: "emerald" | "amber" | "red" | "blue";
}) {
  const tones: Record<string, string> = {
    emerald: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    red: "bg-red-50 text-red-600",
    blue: "bg-blue-50 text-blue-600",
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">
          {label}
        </p>
        <div
          className={cn(
            "w-8 h-8 rounded-lg flex items-center justify-center",
            tones[tone],
          )}
        >
          {icon}
        </div>
      </div>
      <p className="text-2xl font-extrabold text-gray-900">{value}</p>
      <p className="text-[11px] text-gray-500 mt-1 truncate" title={sub}>
        {sub}
      </p>
    </div>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="bg-white border border-dashed border-gray-300 rounded-2xl py-16 text-center">
      <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
        <BookOpen className="h-7 w-7 text-emerald-600" />
      </div>
      <p className="text-lg font-bold text-gray-800">No stories yet</p>
      <p className="text-sm text-gray-500 mt-1 mb-5">
        Create your first story to publish to the mobile feed.
      </p>
      <Button
        onClick={onCreate}
        className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
      >
        <Plus className="h-4 w-4" /> Create Story
      </Button>
    </div>
  );
}

function StoryGridCard({
  story,
  onEdit,
  onToggle,
  onDelete,
}: {
  story: Story;
  onEdit: (s: Story) => void;
  onToggle: (id: number) => void;
  onDelete: (s: Story) => void;
}) {
  const cat =
    CATEGORY_LABELS[story.category as Category] ?? CATEGORY_LABELS.PLAYER_STORY;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden flex flex-col">
      {/* Cover */}
      <div className="relative aspect-[16/9] bg-gray-100">
        {story.coverImage ? (
          <img
            src={story.coverImage}
            alt={story.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300">
            <ImageIcon className="h-8 w-8" />
          </div>
        )}
        {/* Status pill top-left */}
        <div className="absolute top-2 left-2 flex gap-1">
          {story.isPublished ? (
            <span className="bg-emerald-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full tracking-wide uppercase flex items-center gap-1">
              <Eye className="h-3 w-3" /> Live
            </span>
          ) : (
            <span className="bg-gray-700 text-white text-[10px] font-bold px-2 py-0.5 rounded-full tracking-wide uppercase flex items-center gap-1">
              <EyeOff className="h-3 w-3" /> Draft
            </span>
          )}
        </div>
        {/* Category pill top-right */}
        <span
          className={cn(
            "absolute top-2 right-2 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide",
            cat.pill,
          )}
        >
          {cat.label}
        </span>
      </div>

      {/* Body */}
      <div className="p-3 flex-1 flex flex-col">
        <p className="font-bold text-gray-900 text-sm leading-tight line-clamp-2">
          {story.title}
        </p>
        {story.subtitle && (
          <p className="text-xs text-gray-500 mt-1 line-clamp-2">
            {story.subtitle}
          </p>
        )}

        <div className="flex items-center gap-3 text-[11px] text-gray-500 mt-3">
          <span className="flex items-center gap-1" title="Slides">
            <ImageIcon className="h-3 w-3" />
            {story.slides.length}
          </span>
          <span className="flex items-center gap-1" title="Likes">
            <Heart className="h-3 w-3" />
            {story._count?.likes ?? 0}
          </span>
          <span className="flex items-center gap-1" title="Saves">
            <Bookmark className="h-3 w-3" />
            {story._count?.saves ?? 0}
          </span>
          {story.publishedAt && (
            <span
              className="flex items-center gap-1 ml-auto"
              title="Published"
            >
              <Calendar className="h-3 w-3" />
              {new Date(story.publishedAt).toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
              })}
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(story)}
            className="flex-1 text-xs h-7"
          >
            <Pencil className="h-3 w-3 mr-1" /> Edit
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onToggle(story.id)}
            className={cn(
              "flex-1 text-xs h-7",
              story.isPublished
                ? "text-emerald-700 hover:bg-emerald-50"
                : "text-gray-600",
            )}
          >
            <Power className="h-3 w-3 mr-1" />
            {story.isPublished ? "Unpublish" : "Publish"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(story)}
            className="w-8 h-7 p-0 text-red-500 hover:bg-red-50"
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function StoryListRow({
  story,
  onEdit,
  onToggle,
  onDelete,
}: {
  story: Story;
  onEdit: (s: Story) => void;
  onToggle: (id: number) => void;
  onDelete: (s: Story) => void;
}) {
  const cat =
    CATEGORY_LABELS[story.category as Category] ?? CATEGORY_LABELS.PLAYER_STORY;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-4 flex items-center gap-4">
      {/* Thumbnail */}
      <div className="relative w-24 h-16 rounded-lg bg-gray-100 shrink-0 overflow-hidden">
        {story.coverImage ? (
          <img
            src={story.coverImage}
            alt={story.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300">
            <ImageIcon className="h-5 w-5" />
          </div>
        )}
        {!story.isPublished && (
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
            <span className="text-[9px] text-white font-extrabold tracking-wider uppercase">
              Draft
            </span>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <p className="font-semibold text-gray-900 truncate">{story.title}</p>
          <Badge className={cn("text-[10px]", cat.pill)}>{cat.label}</Badge>
          <Badge className="text-[10px] bg-gray-100 text-gray-500">
            {story.slides.length} slide
            {story.slides.length !== 1 ? "s" : ""}
          </Badge>
        </div>
        {story.subtitle && (
          <p className="text-sm text-gray-500 mb-1 truncate">
            {story.subtitle}
          </p>
        )}
        <div className="flex gap-4 text-xs text-gray-400">
          <span className="flex items-center gap-1">
            <Heart className="h-3 w-3" /> {story._count?.likes ?? 0}
          </span>
          <span className="flex items-center gap-1">
            <Bookmark className="h-3 w-3" /> {story._count?.saves ?? 0}
          </span>
          <span className="flex items-center gap-1">
            <TrendingUp className="h-3 w-3" />
            {(story._count?.likes ?? 0) + (story._count?.saves ?? 0)} engagement
          </span>
          {story.publishedAt && (
            <span>
              Published {new Date(story.publishedAt).toLocaleDateString()}
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-1 shrink-0">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onEdit(story)}
          title="Edit"
        >
          <Pencil className="h-4 w-4 text-gray-400" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onToggle(story.id)}
          title={story.isPublished ? "Unpublish" : "Publish"}
          className={cn(
            story.isPublished
              ? "text-emerald-600 hover:bg-emerald-50"
              : "text-gray-400 hover:bg-gray-100",
          )}
        >
          <Power className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(story)}
          title="Delete"
        >
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      </div>
    </div>
  );
}
